import React, { useMemo } from 'react';
import CustomTooltip from './CustomTooltip';
import { Link } from 'react-router-dom';
import { resolveAtlasArcheo, romanToInt } from '../data/atlas_concordance';

/**
 * Mots vides français pour éviter de confondre des titres de section,
 * des noms français ou des phrases exégétiques avec des lemmes latins de la Vulgate.
 */

/**
 * Dictionnaire des abréviations fréquentes.
 */
const ABBREVIATIONS = {
  'Man. bibl.': 'Manuel Biblique (ou Cours d\'Écriture Sainte)',
  'Dict. de la Bible': 'Dictionnaire de la Bible (F. Vigouroux)',
  'Ant. jud.': 'Antiquités judaïques (Flavius Josèphe)',
  'Guerre des Juifs': 'La Guerre des Juifs (Flavius Josèphe)',
  'De Civ. Dei': 'De Civitate Dei (La Cité de Dieu de saint Augustin)',
  'Targ.': 'Targum (traduction araméenne)',
  'Talm.': 'Talmud',
  'Sept.': 'La Septante (traduction grecque)',
  'LXX': 'La Septante (traduction grecque)',
  'Vulg.': 'La Vulgate (traduction latine)',
  'Pesh.': 'La Peshitta (traduction syriaque)',
  'M. T.': 'Texte Massorétique (hébreu)',
  'Hebr.': 'Texte Hébreu'
};

const FRENCH_STOP_WORDS = new Set([
    'rômeset',
    'tanninim',
    'tanah',

  'raisonnement', 'militaire', 'justesse', 'plein', 'pleine', 'simple', 'officier', 'soldat', 'soldats',
  'le', 'la', 'les', 'un', 'une', 'des', 'ce', 'cet', 'cette', 'ces',
  'celui', 'celle', 'ceux', 'celles', 'chacun', 'chacune', 'aucun', 'aucune',
  'entendent', 'entendra', 'entendant', 'croient', 'disent', 'lisent', 'voient', 'savent',
  'du', 'au', 'aux', 'dans', 'en', 'sur', 'sous', 'par', 'pour', 'avec',
  'sans', 'comme', 'mais', 'ou', 'donc', 'or', 'ni', 'car',
  'que', 'quoi', 'dont', 'où', 'il', 'elle', 'ils', 'elles',
  'nous', 'vous', 'je', 'on', 'sa', 'son', 'ses', 'leur', 'leurs',
  'mon', 'ton', 'notre', 'votre', 'ceci', 'cela',
  'chap', 'chapitre', 'verset', 'versets', 'section', 'partie',
  'premier', 'première', 'deuxième', 'troisième', 'introduction',
  'holocaustes', 'sacrifices', 'victime', 'loi', 'bétail', 'dieu',
  'seigneur', 'saint', 'sainte', 'église', 'prophète', 'auteur',
  'terme', 'mot', 'sens', 'texte', 'passage', 'figure',
  'selon', 'après', 'avant', 'pendant', 'aussi', 'ainsi', 'bien',
  'plus', 'moins', 'très', 'encore', 'toujours', 'souvent', 'ici',
  'autre', 'autres', 'tous', 'tout', 'toute', 'toutes', 'chacun',
  'voyez', 'voy', 'conférez', 'comparez', 'remarquez', 'noter',
  'notons', 'rapprocher', 'rapprochez', 'belle', 'cérémonie', 'accomplie',
  'donataire', 'représentant', 'devant', 'détails', 'importants',
  'puis', 'mots', 'termes', 'terme', 'exemple', 'cas', 'fait', 'genre', 'forme', 'suite', 'manière', 'sorte', 'livret',
  // Termes exégétiques et linguistiques français
  'hébraïsme', 'hébraïsmes', 'hellénisme', 'latinisme', 'araméisme', 'métaphore', 'locution', 'ellipse',
  // Titres et institutions français exclusifs
  'chef', 'chefs', 'princes', 'reine', 'prêtre', 'prêtres', 'lévite', 'lévites',
  'pharisien', 'pharisiens', 'sadducéen', 'sadducéens', 'docteur', 'docteurs',
  'pontife', 'pontifes', 'synagogue', 'sanctuaire'
]);

/**
 * Dictionnaire exégétique des lemmes et expressions latines de la Vulgate
 * avec leur traduction française exacte selon la traduction de Fillion lui-même.
 */
const VULGATE_FILLION_TRANSLATIONS = {
  'hostes': 'Les ennemis',
  'filii captivitatis': 'Les fils de la captivité / Les Juifs revenus de l\'exil',
  'ita ut vos quaerimus': 'Comme vous nous cherchons votre Dieu',
  'ita ut vos quaerimus...': 'Comme vous nous cherchons votre Dieu...',
  'ita ut vos quaerimus deum vestrum': 'Comme vous nous cherchons votre Dieu',
  'immolavimus': 'Nous avons immolé des victimes',
  'a diebus asor-haddan': 'Depuis les jours d\'Asarhaddon',
  'qui adduxit nos': 'Qui nous a amenés ici',
  'nos ipsi soli': 'Nous seuls',
  'exemplar epistolae quam misit jeremias': 'Copie de la lettre que Jérémie envoya aux captifs',
  'exemplar epistolae': 'Copie de la lettre',
  'propter peccata quae peccastis': 'À cause des péchés que vous avez commis devant Dieu',
  'propter peccata vestra': 'À cause de vos péchés',
  'dixit homo cujus obturatus est oculus': 'Voici ce que dit l\'homme dont l\'œil est fermé (ou ouvert)',
  'qui cadit et sic aperiuntur oculi ejus': 'Qui tombe, et qui en tombant a les yeux ouverts',
  'quam pulchra tabernacula tua jacob': 'Que vos pavillons sont beaux, ô Jacob !',
  'ut valles nemorosae': 'Comme des vallées couvertes de grands arbres',
  'ut valles': 'Comme des vallées (elles s\'étalent)',
  'ut horti': 'Comme des jardins le long des fleuves',
  'fluet aqua de situla ejus': 'L\'eau coulera de son seau',
  'tolletur propter agag rex ejus': 'Son roi sera plus élevé qu\'Agag',
  'devorabunt gentes hostes illius': 'Il dévore les nations qui lui sont hostiles',
  'qui benedixerit tibi erit et ipse benedictus': 'Celui qui te bénira sera béni lui-même',
  'mensis iste': 'Ce mois sera pour vous le premier des mois',
  'et ait david': 'Et David dit',
  'mingentem': 'qui pisse [contre le mur] / tout mâle',
  'bithsulie': 'Bethsabée',
  'universa domos': 'Toute la maison',
  'ibi abjectus': 'Là fut rejeté',
  'consotatus... david bethsabee': 'David consola Bethsabée',
  'gtlonitem': 'De Gilon',
  'sermonem... intulit': 'Il adressa la parole',
  'in statione': 'En garde / à son poste',
  'cum absalom ierunt': 'Partirent avec Absalom',
  'deleamus heredem': 'Détruisons l\'héritier',
  'surrexit... et abiit': 'Il se leva et s\'en alla',
  'ut offerret': 'qu\'il offrit',
  'ut deciperet te': 'Pour te tromper',
  'seniores': 'Les anciens',
  'epistolam': 'Une lettre',
  'in fastigio portae': 'Sur le haut de la porte',
  'in fastigio porte': 'Sur le haut de la porte',
  'tu... ex adverso': 'Toi à l\'opposé / en face',
  'mille argenteos': 'Mille pièces d\'argent',
  'decem... siclos': 'Dix sicles',
  'sedens mulo': 'Monté sur un mulet',
  'despondi... centum...': 'J\'ai fiancé cent...',
  'ante faciem tuam': 'Devant ta face',
  'de monte ephraim': 'Du mont Éphraïm',
  'mendacem facimus eum': 'Nous Le faisons menteur',
  'non peccavimus': 'Nous n\'avons pas péché',
  'et verbum ejus non est in nobis': 'Et Sa parole n\'est pas en nous',
  'apparuit nobis': 'Qui nous est apparue',
  'de verbo vitae': 'Concernant le Verbe de vie',
  'de verbo vitæ': 'Concernant le Verbe de vie',
  'ab initio': 'Dès le commencement',
  'vita manifestata est': 'La vie a été manifestée',
  'fuit': 'était',
  'apparuit homo': 'Un homme apparut',
  'ad majores juda': 'Aux anciens de Juda',
  'vocem cantorum': 'La voix des chanteurs',
  'amicus': 'L\'ami',
  'in solario': 'Sur la terrasse / solarium',
  'utre vini': 'Une outre de vin',
  'osculatus... rex...': 'Le roi baisa...',
  'de jezraeli': 'De Jezraël',
  'de cognatione... saul': 'De la parenté de Saül',
  'in virga virorum': 'Avec la verge des hommes',
  'stabiliam... in sempiternum': 'J\'affermirai pour toujours',
  'ad aream nachon': 'Jusqu\'à l\'aire de Nachon',
  'per gyrum': 'Tout autour',
  'habitavit': 'Il habita',
  'caecus et claudus...': 'L\'aveugle et le boiteux...',
  'civitas david': 'La cité de David',
  'inebriavit...': 'Il enivra...',
  'per salutem...': 'Par le salut / la vie de...',
  'filio isai': 'Au fils d\'Isaï',
  'ostiaria... obdormivit': 'La portière s\'endormit',
  'venientes...': 'Venant / Arrivant...',
  'statio...': 'La station / le poste...',
  'tempore messis': 'Au temps de la moisson',
  'saltem amnon': 'Au moins Amnon',
  'benedixit et': 'Il bénit et',
  'vocavit... omnes...': 'Il appela tous...',
  'non locutus...': 'N\'ayant pas parlé...',
  'venit nuntius': 'Le messager vint',
  'caesis latronibus': 'Les brigands ayant été tués',
  'omnia quae...': 'Toutes les choses qui...',
  'ce. rethi et phelethi': 'Les Céréthiens et les Phéléthiens',
  'cerethi et phelethi': 'Les Céréthiens et les Phéléthiens',
  'scriba': 'Le scribe',
  'sacerdotes': 'Les prêtres',
  'reliquerunt sculptilia': 'Ils abandonnèrent leurs idoles',
  'reversusque...': 'Et s\'en étant retourné...',
  'fortium': 'Des forts / vaillants',
  'nocte': 'De nuit',
  'filia ncircumcisorum': 'La fille des incirconcis',
  'filia incircumcisorum': 'La fille des incirconcis',
  'quem unximus': 'Que nous avons oint',
  'sagitta...': 'La flèche...',
  'viri juda': 'Les hommes de Juda',
  'in oppidis hebron': 'Dans les villes d\'Hébron',
  'consuluit dominum': 'Il consulta le Seigneur',
  'surrexit... mane': 'Il se leva le matin',
  'ad joab': 'À Joab',
  'suavem odorem': 'une agréable odeur',
  'numquid': 'est-ce que',
  'salomon': 'Salomon',
  'sorbitiunculas': 'des bouillons / petites soupes',
  'veniat': 'qu\'il vienne',
  'vir prudens': 'un homme prudent',
  'qui portabant': 'ceux qui portaient',
  'cum gaudio': 'avec joie',
  'quod benedixisset': 'qu\'il avait béni',
  'fugiamus': 'fuyons',
  'toto corde': 'de tout cœur',
  'deperiret': 'dépérissait',
  'in typo laterum': 'dans des moules à briques',
  'cultris': 'avec des couteaux / haches',
  'ferrata carpenta': 'chariots armés de fer',
  'david remansit': 'David resta',
  'rabba': 'Rabba',
  'tempore quo': 'au temps où',
  'vertente anno': 'l\'année tournant / au retour de l\'année',
  'artifices': 'des artisans',
  'misit': 'il envoya',
  'abiit ergo': 'il s\'en alla donc',
  'venitque': 'et il vint',
  'in porta': 'à la porte',
  'inibis praelium': 'tu iras au combat',
  'tres qui erant': 'trois qui étaient',
  'requiem dedisset': 'avait donné le repos',
  'fartum est': 'il a été fait / préparé',
  'dicentes': 'disant',
  'dixerunt': 'ils dirent',
  'filius triginta': 'fils de trente ans',
  'armillam': 'le bracelet',
  'tuli diadema': 'j\'ai pris le diadème',
  'stansque occidi': 'et me tenant debout je l\'ai tué',
  'adhuc tota anima': 'mon âme est encore tout entière en moi',
  'angustia': 'l\'angoisse / les crampes',
  'caessavitque plaga': 'et la plaie cessa',
  'quinquaginta siclis': 'cinquante sicles',
  'absit': 'qu\'il soit loin de moi',
  'aream et boves': 'l\'aire et les bœufs',
  'plaustrum': 'le chariot / train de bois',
  'venit gad': 'Gad vint',
  'isti qui oves': 'ceux-ci qui sont des brebis',
  'ego sum': 'c\'est moi',
  'jebusaei': 'du Jébuséen',
  'juxta aream': 'près de l\'aire',
  'misertus est': 'eut compassion / se repentit',
  'angelus domini': 'l\'ange du Seigneur',
  'septuaginta millia': 'soixante-dix mille',
  'ad tempus constitutum': 'jusqu\'au temps fixé',
  'melius est': 'il vaut mieux',
  'septem annis': 'sept années',
  'datur optio': 'le choix te est donné',
  'surrexit mane': 'il se leva le matin',
  'stulte egi': 'j\'ai agi follement',
  'peccavi valde': 'j\'ai grandement péché',
  'percussit cor': 'le cœur [de David] fut frappé / battit',
  'in conspectu': 'devant les yeux',
  'quantus nunc est': 'quelque nombreux qu\'il soit maintenant',
  'dixitque joab': 'et Joab dit',
  'dixitque rex': 'et le roi dit',
  'commovitque': 'et il incita',
  'addidit furor': 'la fureur du Seigneur s\'ajouta',
  'addidit furor domini irasci': 'la fureur du Seigneur s\'ajouta à s\'irriter',
  'post mortem josue': 'Après la mort de Josué',
  'consuluerunt dominum': 'consultèrent le Seigneur',
    'creavit ut faceret': 'qu\'il créa pour faire',
    'creavit producendo': 'créa en produisant',
    'repentem': 'rampant (qui se meut)',
    'et dividat': 'Et qu\'il sépare',
    'herbam virentem': 'herbe verte',
    'juxta genus': 'selon son espèce',
    'dividant diem': 'qu\'ils séparent le jour',
    'sint in signa': 'qu\'ils servent de signes',
    'dies et annos': 'les jours et les années',
    'illuminent terram': 'qu\'ils éclairent la terre',
    'duo luminaria magna': 'deux grands luminaires',
    'producant aquae': 'Que les eaux produisent',
    'reptile': 'ce qui rampe',
    'cete grandia': 'les grands animaux aquatiques',
    'omnem animam... motabilem': 'tous les animaux qui se meuvent dans les eaux',
    'quam produxerant': 'dont avaient pullulé',
    'in species... secundum genus': 'selon leur espèce',
    'crescite': 'fructifiez',
    'avesque super terram': 'Et les oiseaux sur la terre',
    'sub firmamento caeli': 'sous le firmament du ciel',
    'producat terra': 'Que la terre produise',
    'animam viventem': 'des animaux vivants',
    'reptilia': 'ce qui rampe',
    'faciamus': 'Faisons',
    'hominem': 'L\'homme',
    'ad imaginem et similitudinem': 'A notre image et ressemblance',
    'et praesit': 'qu\'il domine',

  // Pentateuque & Lévitique
  'in diversorio': 'dans l\'hôtellerie',
  'super fluvios eorum et rivos': 'sur leurs fleuves et leurs ruisseaux',
  'pauperis non misereberis in judicio': 'tu n\'auras point égard au pauvre dans son procès',
  'viam trium dierum in solitudinem': 'trois journées de marche dans le désert',
  'nec os illius confringetis': 'vous ne lui briserez aucun os',
  'in desertum sin': 'au désert de Sin',
  'locutusque est dominus cunctos sermones hos': 'le Seigneur prononça toutes ces paroles',
  'acutissimam petram': 'une pierre très aiguë (tranchante)',
  'sponsus sanguinum': 'époux de sang',
  'ego indurabo cor ejus': 'j\'endurcirai son cœur',
  'filius meus primogenitus': 'mon fils premier-né',
  'versa est in sanguinem': 'fut changée en sang',
  'ad vesperam': 'au soir',
  'cave ne ultra videas faciem meam': 'prends garde de ne plus revoir ma face',
  'induratumque est cor pharaonis': 'le cœur de Pharaon s\'endurcit',
  'ego enim dominus sanator tuus': 'car je suis le Seigneur qui te guérit',
  'non credent': 'ils ne me croiront point',
  'virga': 'une verge',
  'castrametata est in raphidim': 'elle campa à Raphidim',
  'ego sum deus patris tui': 'je suis le Dieu de ton père',
  'et credidit populus': 'et le peuple crut',
  'educam vos... et induxerim': 'je vous retirerai... et je vous introduirai',
  'non assumes nomen domini... in vanum': 'tu ne prendras point le nom du Seigneur en vain',
  'venerunt autem in elim': 'ils vinrent à Elim',
  'jethro socerum suum': 'Jéthro son beau-père',
  'mitte quem missurus es': 'envoyez celui que vous devez envoyer',
  'iratus dominus': 'le Seigneur étant irrité',
  'ipse loquetur pro te ad populum': 'il parlera pour toi au peuple',
  'vade ad eum mane... ad aquas': 'va vers lui le matin... vers les eaux',
  'non remanebit ex eis ungula': 'il n\'en restera pas un ongle',
  'signum in manu tua': 'un signe sur ta main',
  'in columna nubis... in columna ignis': 'dans une colonne de nuée... dans une colonne de feu',
  'cantemus domino... dominus quasi vir pugnator': 'chantons au Seigneur... le Seigneur est comme un guerrier',
  'maria prophetis... cum tympanis et choris': 'Marie la prophétesse... avec des tambourins et des chœurs',
  'scribe hoc ob monumentum in libro': 'écris ceci pour mémorial dans un livre',
  'non habebis deos alienos': 'tu n\'auras point d\'autres dieux',
  'ad deos': 'devant Dieu / devant les juges',
  'non occides': 'tu ne tueras point',
  'non furtum facies': 'tu ne déroberas point',
  'non loqueris falsum testimonium': 'tu ne diras point de faux témoignage',
  'de ramesse in socoth': 'de Ramsès à Socoth',
  'salutaverunt se mutuo': 'ils se saluèrent mutuellement',
  'apparui abraham... in deo omnipotente': 'je suis apparu à Abraham... comme le Dieu tout-puissant',
  'induravitque dominus': 'et le Seigneur endurcit',
  'in hac vice mittam omnes plagas meas': 'cette fois j\'enverrai toutes mes plaies',
  'grando et ignis mista pariter': 'de la grêle et du feu mêlés ensemble',
  'vidi afflictionem... et sciens dolorem ejus descendi': 'j\'ai vu l\'affliction... et connaissant sa douleur je suis descendu',
  'super petram horeb': 'sur le rocher d\'Horeb',
  'peccavi etiam nunc... dominus justus': 'j\'ai péché cette fois... le Seigneur est juste',
  'vespere scietis... et mane videbitis gloriam domini': 'ce soir vous saurez... et demain matin vous verrez la gloire du Seigneur',
  'leprosam instar nivis': 'lépreuse comme la neige',
  'sermonem signi': 'la voix du prodige',
  'non sum eloquens': 'je n\'ai point la parole facile',
  'ab heri et nudiustertius': 'depuis hier et avant-hier',
  'mortui sunt enim omnes qui quaerebant animam tuam': 'car tous ceux qui en voulaient à ta vie sont morts',
  'virgam dei': 'la verge de Dieu',
  'veneruntque simul': 'ils vinrent donc ensemble',
  'vocavit... dominus': 'Le Seigneur appela',
  'vocavit dominus': 'Le Seigneur appela',
  'de tabernaculo': 'du tabernacle',
  'de pecoribus': 'de bêtes à quatre pieds (du bétail)',
  'si holocaustum': 'Si son oblation est un holocauste',
  'oblatio': 'l\'offrande (oblation)',
  'de armento': 'de gros bétail',
  'masculum': 'un mâle',
  'immaculatum': 'sans tache (sans défaut)',
  'ad ostium tabernaculi': 'à la porte du tabernacle',
  'ad placandum': 'pour se rendre favorable [le Seigneur]',
  'ponet... manum': 'Il mettra la main',
  'ponet manum': 'Il mettra la main',
  'immolabit... vitulum': 'et il immolera le veau',
  'immolabitque vitulum': 'et il immolera le veau',
  'fundentque sanguinem': 'et ils répandront le sang',
  'per altaris circuitum': 'autour de l\'autel',
  'detractaque pelle': 'ayant dépouillé la victime',
  'in expiationem': 'en expiation',
  'de bobus': 'de gros bétail',
  'ovibus': 'de petit bétail (brebis)',
  'hostiam': 'une hostie (victime)',
  'suavem odorem domino': 'une agréable odeur au Seigneur',
  'panem dei sui': 'le pain de son Dieu',
  'sanctum sanctorum': 'le Saint des Saints',
  'lex carnalis': 'la loi charnelle',
  'ex opere operato': 'par l\'efficacité même de l\'acte',
  'in odorem suavitatis': 'en odeur de suavité',
  'pro delicto': 'pour le délit',
  'pro peccato': 'pour le péché',

  'in filio': 'dans le Fils',
  'creare ex nihilo': 'créer de rien (ex nihilo)',
  'bara\'': 'créer',
  '\'āśāh': 'faire',
  'yaṣar': 'former',
  'banah': 'bâtir',
  'qal': 'forme Qal (active)',
  'niphal': 'forme Niphal (passive)',
  '\'elohim': 'Dieu (pluriel de majesté)',
  '\'elohah': 'Dieu (forme poétique)',
  '\'ul': 'être fort',
  '\'alah': 'craindre, vénérer',

  // Genèse & Création
  'in principio': 'Au commencement',
  'in principio creavit deus': 'Au commencement Dieu créa',
  'creavit': 'créa',
  'deus': 'Dieu',
  'coelum et terram': 'le ciel et la terre',
  'cœlum et terram': 'le ciel et la terre',
  'coelum': 'ciel',
  'cœlum': 'ciel',
  'terra autem': 'Or la terre',
  'inanis et vacua': 'informe et nue (vide et déserte)',
  'vastitas et inanitas': 'dévastation et vide',
  'et tenebrae': 'et les ténèbres',
  'et tenebræ': 'et les ténèbres',
  'et ienebræ': 'et les ténèbres',
  'faciem abyssi': 'la face de l\'abîme',
  'spiritus': 'l\'Esprit',
  'spiritus dei': 'l\'Esprit de Dieu',
  'dei': 'de Dieu',
  'montes dei': 'les montagnes de Dieu',
  'cedri dei': 'les cèdres de Dieu',
  'montes dei cedri dei': 'montagnes de Dieu, cèdres de Dieu',
  'volitans incubans': 'voltigeant, couvant',
  'volitans': 'voltigeant',
  'incubans': 'couvant',
  
  // Genèse 3
  'serpens': 'le serpent',
  'callidior': 'plus rusé',
  'dixit ad mulierem': 'il dit à la femme',
  'cur præcepit': 'pourquoi a-t-il commandé',
  'de omni ligno': 'de tout arbre',
  'respondit': 'répondit',
  'de fructu lignorum': 'du fruit des arbres',
  'et ne tangeremus': 'et de ne pas le toucher',
  'forte': 'de peur que (peut-être)',
  'nequaquam morte': 'vous ne mourrez nullement',
  'scit enim': 'car [Dieu] sait',
  'aperientur oculi': 'les yeux s\'ouvriront',
  'sicut dii': 'comme des dieux',
  'sicut deus': 'comme Dieu',
  'vidit igitur': 'elle vit donc',
  'bonum': 'bon',
  'pulchrum': 'beau',
  'delectabile': 'agréable',
  'tulit': 'elle prit',
  'comedit': 'mangea',
  'eva parens, quid fecisti ?': 'Mère Ève, qu\'as-tu fait ?',
  'deditque viro': 'et elle en donna au mari',
  'et aperti sunt': 'et furent ouverts',
  'folia ficus': 'des feuilles de figuier',
  'cum audissent': 'quand ils eurent entendu',
  'deambulantis': 'qui se promenait',
  'ad auram': 'à la brise',
  'abscondit se': 'se cacha',
  'maledictus es': 'tu es maudit',
  'inter omnia animantia': 'entre tous les animaux',
  'et bestias terræ': 'et les bêtes de la terre',
  'super pectus': 'sur ta poitrine',
  'terram comedes': 'tu mangeras de la terre',
  'inimicitias': 'des inimitiés',
  'inter te et mulierem': 'entre toi et la femme',
  'semen illius': 'sa race (sa postérité)',
  'ipsa conteret': 'elle écrasera',
  'semen': 'race (postérité)',
  'mulier': 'la femme',
  'tu insidiaberis calcaneo': 'tu dresseras des embûches à son talon',
  'ipse conteret': 'il écrasera',
  'tu conteres': 'tu écraseras',
  'mulieri quoque': 'à la femme aussi',
  'multiplicabo': 'je multiplierai',
  'aerumnas': 'les misères',
  'gravida et parturiens est sicut aegrota et moriens': 'la femme enceinte et celle qui enfante sont comme la malade et la mourante',
  'sub viri': 'sous [le pouvoir] du mari',
  'et ipse dominabitur': 'et il dominera',
  'sub viri potestate eris': 'tu seras sous la puissance du mari',
  'et ad virum tuum desiderium tuum': 'et ton désir se portera vers ton mari',
  'quia audisti': 'parce que tu as écouté',
  'maledicta terra': 'la terre est maudite',
  'in opere tuo': 'dans ton travail',
  'in laboribus': 'dans les travaux',
  'comedes ex ea': 'tu en mangeras',
  'spinas et tribulos': 'des épines et des ronces',
  'in sudore vultus': 'à la sueur du visage',
  'donec revertaris': 'jusqu\'à ce que tu retournes',
  'et vocavit': 'et il appela',
  'fecit quoque deus': 'Dieu fit aussi',
  'tunicas pelliceas': 'des tuniques de peau',
  'et ait': 'et il dit',
  'quasi unus ex nobis': 'comme l\'un de nous',
  'nunc ergo, ne': 'maintenant donc, [prenons garde] que...',
  'et emisit eum': 'et il le renvoya',
  'ejecitque': 'et il chassa',
  'emisit': 'il renvoya',
  'ante paradisum': 'devant le paradis',
  'cherubim': 'les Chérubins',
  'flammeum gladium': 'un glaive de feu',
  'versatilem': 'tournoyant (qui se tournait çà et là)',
  'ferebatur': 'était porté',
  'super aquas': 'sur les eaux',
  'dixitque deus': 'Or Dieu dit',
  'fiat lux': 'Que la lumière soit',
  'et facta est lux': 'et la lumière fut',
  'ipse dixit et facta sunt': 'Il a parlé, et tout a été fait',
  'vidit deus quod esset bonum': 'Dieu vit que cela était bon',
  'et factum est vespere et mane': 'et du soir et du matin se fit',
  'divisit lucem': 'sépara la lumière',
  'appellavitque lucem': 'et il appela la lumière',
  'diem': 'jour',
  'noctem': 'nuit',
  'firmamentum': 'le firmament (l\'étendue)',
  'et dividat': 'et qu\'il sépare',
  'et fecit': 'et il fit',
  'aquas... sub firmamento': 'les eaux sous le firmament',
  'et factum est ita': 'et il fut fait ainsi',
  'cselum': 'ciel',
  'caelum': 'ciel',
  'congregentur... in locum unum': 'qu\'elles se rassemblent en un seul lieu',
  'appareat arida': 'qu\'apparaisse le sec (la terre ferme)',
  'terram': 'la terre',
  'maria': 'les mers',
  'germinet terra': 'que la terre produise',
  'herbam virentem': 'de l\'herbe verdoyante',
  'facientem semen': 'qui porte sa semence',
  'fiant luminaria': 'qu\'il y ait des luminaires',
  'duo luminaria magna': 'deux grands luminaires',
  'et stellas': 'et les étoiles',
  'producant aquae': 'que les eaux produisent',
  'omne reptile': 'tout animal qui rampe',
  'beneditxit eis': 'il les bénit',
  'crescite': 'croissez (fructifiez)',
  'producat terra': 'que la terre produise',
  'animam viventem': 'des êtres vivants',
  'faciamus': 'faisons',
  'hominem': 'l\'homme',
  'ad imaginem et similitudinem': 'à notre image et à notre ressemblance',
  'et praesit': 'et qu\'il commande',
  'et creavit': 'et il créa',
  'benedixitque illis': 'et il les bénit',
  'omnem herbam': 'toute herbe',
  'vobis in escam': 'pour vous servir de nourriture',
  'et cunctis animantibus': 'et à tous les animaux',
  'erant valde bona': 'étaient très bons',
  'igitur perfecti sunt': 'Ainsi furent achevés',
  'complevitque deus': 'et Dieu acheva',
  'requievit': 'il se reposa',
  'benedixit diei': 'il bénit le jour',
  'sanctificavit illum': 'il le sanctifia',

  // Évangiles & Nouveau Testament
  'liber generationis': 'Livre de la généalogie',
  'liber generationis jesu christi': 'Livre de la généalogie de Jésus-Christ',
  'filii david': 'fils de David',
  'filii abraham': 'fils d\'Abraham',
  'abraham genuit isaac': 'Abraham engendra Isaac',
  'christi autem generatio': 'Or la naissance du Christ',
  'vir ejus': 'son époux (son fiancé)',
  'cum esset desponsata': 'comme elle était fiancée',
  'inventa est in utero habens': 'elle se trouva ayant conçu',
  'de spiritu sancto': 'du Saint-Esprit',
  'voluit occulte dimittere eam': 'voulut la renvoyer secrètement',
  'noli timere accipere mariam': 'ne crains point de prendre Marie',
  'pariet autem filium': 'Elle enfantera un fils',
  'vocabis nomen ejus jesum': 'tu lui donneras le nom de Jésus',
  'ipsum enim salvum faciet': 'car c\'est lui qui sauvera',
  'populum suum a peccatis eorum': 'son peuple de ses péchés',
  'hoc autem totum factum est': 'Or tout cela arriva',
  'ut adimpleretur': 'pour que s\'accomplît',
  'ecce virgo in utero habebit': 'Voici, la vierge concevra',
  'nobiscum deus': 'Dieu avec nous (Emmanuel)',
  'cum natus esset jesus': 'Jésus étant né',
  'in bethlehem judae': 'à Bethléem de Juda',
  'in diebus herodis regis': 'aux jours du roi Hérode',
  'ecce magi ab oriente': 'voici des Mages d\'Orient',
  'ubi est qui natus est': 'Où est le nouveau-né',
  'rex judaeorum': 'roi des Juifs',
  'vidimus enim stellam ejus': 'car nous avons vu son étoile',
  'in oriente': 'en Orient',
  'et venimus adorare eum': 'et nous sommes venus l\'adorer',
  'audiens autem herodes': 'Le roi Hérode, l\'apprenant',
  'turbatus est': 'fut troublé',
  'et congregans omnes principes': 'Et rassemblant tous les grands prêtres',
  'sciscitabatur ab eis': 'il leur demanda',
  'ubi christus nasceretur': 'où le Christ devait naître',
  'et tu bethlehem terra juda': 'Et toi, Bethléem, terre de Juda',
  'nequaquam minima es': 'tu n\'es nullement la moindre',
  'in principibus juda': 'parmi les principales villes de Juda',
  'tunc herodes clam vocatis magis': 'Alors Hérode, ayant appelé en secret les Mages',
  'diligenter didicit ab eis': 's\'informa exactement auprès d\'eux',
  'tempus stellae': 'du temps où l\'étoile était apparue',
  'ite et interrogate diligenter': 'Allez et informez-vous exactement',
  'de puero': 'sur le petit enfant',
  'qui cum audissent regem': 'Ceux-ci, ayant entendu le roi',
  'abierunt': 'partirent',
  'et ecce stella': 'et voici que l\'étoile',
  'praecedebat eos': 'les précédait',
  'usque dum veniens staret': 'jusqu\'à ce qu\'elle vînt s\'arrêter',
  'ubi erat puer': 'où était l\'enfant',
  'videntes autem stellam': 'Et voyant l\'étoile',
  'gavisi sunt gaudio magno': 'ils se réjouirent d\'une très grande joie',
  'intrantes domum': 'entrant dans la maison',
  'invenerunt puerum': 'ils trouvèrent l\'enfant',
  'cum maria matre ejus': 'avec Marie sa mère',
  'procidentes adoraverunt eum': 'se prosternant, ils l\'adorèrent',
  'apertis thesauris suis': 'ayant ouvert leurs trésors',
  'obtulerunt ei munera': 'ils lui offrirent des présents',
  'aurum thus et myrrham': 'de l\'or, de l\'encens et de la myrrhe',
  'in principio erat verbum': 'Au commencement était le Verbe',
  'verbum erat apud deum': 'le Verbe était avec Dieu',
  'deus erat verbum': 'le Verbe était Dieu',
  'fuit homo missus a deo': 'Il y eut un homme envoyé de Dieu',
  'cui nomen erat joannes': 'dont le nom était Jean',
  'et lux in tenebris lucet': 'Et la lumière luit dans les ténèbres',
  'et tenebrae eam non comprehenderunt': 'et les ténèbres ne l\'ont point comprise',
  'surge': 'Lève-toi',
  'licet': 'Est-il permis',
  'circumspiciens': 'regardant à l\'entour',
  'cum ira': 'avec colère',
  'contristatus': 'attristé',
  'super caecitate': 'sur l\'aveuglement (la dureté)',
  'ecce agnus dei': 'Voici l\'Agneau de Dieu',
  'magnificat anima mea dominum': 'Mon âme glorifie le Seigneur',
  'miratus est': 'Il admira',
  'non inveni tantam fidem': 'Je n\'ai pas trouvé une si grande foi',
  'in israel': 'en Israël',
  'trans fretum': 'au delà du détroit (sur l\'autre rive)',
  'in partes caesareae philippi': 'au territoire de Césarée de Philippe',
  'quem dicunt homines esse': 'Qui disent les hommes qu\'est',
  'filium hominis': 'le Fils de l\'homme',
  'tu es christus': 'Tu es le Christ',
  'filius dei vivi': 'le Fils du Dieu vivant',
  'beatus es simon bar jona': 'Tu es bienheureux, Simon, fils de Jonas',
  'quia caro et sanguis': 'car la chair et le sang',
  'non revelavit tibi': 'ne te l\'ont point révélé',
  'sed pater meus': 'mais mon Père',
  'qui in caelis est': 'qui est dans les cieux',
  'tu es petrus': 'Tu es Pierre',
  'et super hanc petram': 'et sur cette pierre',
  'aedificabo ecclesiam meam': 'je bâtirai mon Église',
  'et portae inferi': 'et les portes de l\'enfer',
  'non praevalebunt adversus eam': 'ne prévaudront point contre elle',
  'tibi dabo claves': 'Je te donnerai les clefs',
  'regni caelorum': 'du royaume des cieux',
  'quodcumque ligaveris': 'tout ce que tu lieras',
  'erit ligatum': 'sera lié',
  'quodcumque solveris': 'tout ce que tu délieras',
  'erit solutum': 'sera délié',
  'tunc praecepit discipulis': 'Alors il commanda à ses disciples',
  'exinde coepit jesus': 'Dès lors Jésus commença',
  'monstrare discipulis suis': 'à montrer à ses disciples',
  'quia oporteret eum ire': 'qu\'il lui fallait aller',
  'et multa pati': 'et souffrir beaucoup',
  'et occidi': 'et être mis à mort',
  'et tertia die resurgere': 'et ressusciter le troisième jour',
  'vae tibi corozain': 'Malheur à toi, Corozaïn',
  'vae tibi bethsaida': 'malheur à toi, Bethsaïda',
  'in cilicio et cinere': 'dans le sac et la cendre',
  'remissius erit': 'sera traitée avec moins de rigueur',
  'numquid usque in caelum': 't\'élèveras-tu jusqu\'au ciel ?',
  'confiteor tibi pater': 'Je te loue, ô Père',
  'venite ad me omnes': 'Venez à moi, vous tous',
  'qui laboratis et onerati estis': 'qui êtes fatigués et chargés',
  'et ego reficiam vos': 'et je vous soulagerai',
  'tollite jugum meum': 'Prenez mon joug',
  'jugum enim meum suave est': 'Car mon joug est doux',
  'et onus meum leve': 'et mon fardeau léger',

  // Prophètes, Psaumes & Sagesse
  'transmigratio... israel': 'les captifs d\'Israël',
  'filii ammon et moab': 'les fils d\'Ammon et de Moab',
  'custodes fontium': 'les gardiens des fontaines',
  'dum circuit': 'en faisant la ronde',
  'ad refocillandum potius': 'plutôt pour se rafraîchir',
  'retributio multa': 'une grande récompense',
  'delicta quis': 'qui connaît ses égarements ?',
  'parce servo tuo': 'épargne ton serviteur',
  'a delicto maximo': 'du plus grand péché',
  'vivent tritico': 'ils vivront de froment',
  'convertere ad': 'reviens vers',
  'vitulos labiorum': 'les sacrifices de nos lèvres',
  'quasi lilium': 'comme le lis',
  'quasi oliva': 'comme l\'olivier',
  'exsurge': 'Lève-toi',
  'redime... propter nomen': 'délivre-nous à cause de ton nom',
  'ad intellectum': 'pour donner l\'intelligence (psaume didactique)',

  // Malachie III
  'ecce ego': 'Voici que moi / Voici que',
  'mitto': 'j\'envoie',
  'angelum meum': 'mon ange / mon messager',
  'praeparabit viam': 'il préparera la voie',
  'et statim': 'et aussitôt / et soudain',
  'ad templum': 'à son temple / en son palais',
  'dominator': 'le Dominateur / le Seigneur Souverain',
  'angelus testamenti': 'l\'ange de l\'alliance / le messager de l\'alliance',
  'ecce venit': 'Voici qu\'il vient',
  'quis poterit': 'Qui pourra [penser au jour...]',
  'quis poterit cogitare': 'Qui pourra penser [au jour...]',
  'quis stabit': 'qui se tiendra debout',
  'quis stabit ad': 'qui pourra soutenir sa vue / se tenir debout',
  'ipse enim': 'Car lui-même / Car il sera',
  'quasi ignis': 'comme le feu',
  'quasi ignis conflans': 'comme le feu qui fond [les métaux]',
  'quasi herba': 'comme l\'herbe',
  'quasi herba fullonum': 'comme l\'herbe des foulons',
  'sedebit conflans': 'il s\'assiéra comme celui qui fond',
  'purgabit': 'il purifiera',
  'purgabit... levi': 'il purifiera les fils de Lévi',
  'colabit': 'il passera au filtre / il épurera',
  'erunt': 'ils seront',
  'erunt... offerentes... in justitia': 'ils offriront des sacrifices dans la justice',
  'sacrificia': 'les sacrifices / offrandes',
  'et placebit': 'Et sera agréable / et plaira',
  'sicut dies saeculi': 'comme aux jours d\'autrefois',
  'et accedam': 'Et je m\'approcherai',
  'in judicio': 'en jugement / pour le châtiment',
  'testis velox': 'témoin prompt / juge rapide',
  'maleficis': 'aux magiciens / aux sorciers',
  'canticum pro dilecto': 'chant d\'amour pour le bien-aimé',
  'eructavit': 'mon cœur a bouillonné'
};

/**
 * Cherche la traduction française d'un lemme ou extrait de la Vulgate.
 */
function findTranslation(rawLemma) {
  if (!rawLemma) return null;
  const clean = rawLemma
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/œ/g, 'oe')
    .replace(/[\.\,\:\;\(\)\«\»\-\–\—]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Correspondance exacte
  if (VULGATE_FILLION_TRANSLATIONS[clean]) {
    return VULGATE_FILLION_TRANSLATIONS[clean];
  }

  // Correspondance avec suspension (ex: 'vocavit... dominus')
  const words = clean.split(' ').filter(Boolean);
  if (words.length >= 2) {
    const withDots = `${words[0]}... ${words[words.length - 1]}`;
    if (VULGATE_FILLION_TRANSLATIONS[withDots]) {
      return VULGATE_FILLION_TRANSLATIONS[withDots];
    }
  }

  return null;
}

function isTranslationRedundant(translation, restText) {
  if (!translation || !restText) return false;
  
  // Règle 45: La traduction ne doit être masquée QUE si le texte est immédiatement suivi 
  // d'une virgule (ou point, tiret) et du mot exact de la traduction.
  
  const cleanTrans = translation.toLowerCase().trim();
  if (!cleanTrans) return false;

  // Extraire les mots de la traduction complète sans ponctuation pour la comparaison exacte
  const rawTransWords = cleanTrans.replace(/[\(\)\[\]\.\,]/g, ' ').split(/\s+/).filter(Boolean);
  if (rawTransWords.length === 0) return false;

  // 1. Chercher si le texte commence par la traduction exacte, 
  // en tolérant la ponctuation initiale (virgule, espace, tiret, guillemets, parenthèse, deux-points, etc)
  const m = restText.match(/^[\s\.\,\;\:\)\(\]\[\-\—\»\«\…]+([a-zàâéèêëîïôöùûüç\'\-\s]+)/i);
  if (m) {
    const immediateFollowingText = m[1].toLowerCase();
    
    // Si la traduction exacte figure juste après la ponctuation
    if (immediateFollowingText.startsWith(cleanTrans)) return true;

    // Si on a un seul mot de traduction et que c'est le premier mot (ex: yaṣar, former)
    const followingWords = immediateFollowingText.split(/\s+/);
    if (followingWords[0] === rawTransWords[0] && rawTransWords.length === 1) {
       return true;
    }
  }

  return false;
}

const HEBREW_KNOWN_TERMS = new Set([
    'rômeset',
    'tanninim',
    'tanah',

  "bara'", "'āśāh", "yaṣar", "banah", "qal", "niphal", "piel", "hithpael",
  "'elohim", "'elohah", "'ul", "'alah",
  "tôhu", "vabôhu", "tohu-bohu", "t'hôm", "ṭ'hôm", "ruah", "ruaḥ",
  "m'raḥefet", "m'raḥéfèt", "šāmayim", "'ereṣ", "tôhu va-bôhu",
  "y'hi 'or", "y'hi 'ôr", "vayy'hi 'or", "vayy'hi 'ôr", "tôb", "raqîa'", "raqîa"
]);

function isHebrewTerm(phrase, beforeContext = '') {
  if (!phrase) return false;
  const clean = phrase.toLowerCase().trim();
  if (HEBREW_KNOWN_TERMS.has(clean)) return true;

  // Détection contextuelle : si précédé immédiatement par "hébreu", "araméen", etc.
  if (beforeContext && /(?:h[ée]breu|aram[ée]en|chald[ée]en|syriaque|rabbinique)\s*[:\s(]*$/i.test(beforeContext)) {
    return true;
  }

  // Sous-mots hébreux connus (ex: 'tôhu vabôhu')
  const words = clean.split(/[\s\-]+/);
  if (words.some(w => HEBREW_KNOWN_TERMS.has(w))) return true;

  // Caractères diacritiques sémitiques spécifiques (inexistants en latin classique : macrons, points souscrits, circonflexes sémitiques)
  if (/[āēīōūṣṭḥḍḳšśʔ’']/.test(clean) || clean.includes("ô") || clean.includes("ê") || clean.includes("î") || clean.includes("û")) {
    if (clean.includes("vastitas") || clean.includes("inanitas") || clean.includes("significavit")) return false;
    return true;
  }

  // Terminaisons hébraïques typiques (pluriels -im, -oth) si non latin
  if ((clean.endsWith("im") || clean.endsWith("oth")) && !clean.endsWith("enim")) {
    return true;
  }
  return false;
}

// Mots partagés entre latin et français ou fréquents dans la Vulgate
const LATIN_COMMON_WORDS = new Set([
  'et', 'qui', 'tu', 'me', 'te', 'se', 'nos', 'vos', 'ut', 'mille', 'david', 'amen', 'radix', 'pauper',
  'aaron', 'moyses', 'moises', 'israel', 'ephraim', 'noe', 'cain', 'adam', 'salomon', 'hierusalem', 'sion', 'jacob', 'abraham', 'isaac', 'judas'
]);

const LATIN_STOP_WORDS = new Set([
  'et', 'in', 'ad', 'de', 'cum', 'ab', 'ex', 'per', 'sub', 'super', 'pro', 'ut',
  'qui', 'quae', 'quod', 'quem', 'cuius', 'cui', 'quo', 'qua',
  'non', 'sed', 'autem', 'enim', 'ergo', 'itaque', 'vero', 'nam',
  'omnis', 'omnes', 'omnia', 'suus', 'sua', 'suum', 'eum', 'eam', 'eos', 'eas',
  'dixit', 'ait', 'fecit', 'venit', 'audivit', 'vidit', 'posuit', 'erat', 'fuit', 'sunt',
  'est', 'prodest', 'adest', 'potest', 'possunt'
]);

const LATIN_ENDINGS = [
  'us', 'um', 'is', 'os', 'as', 'es', 'ibus', 'orum', 'arum',
  'it', 'unt', 'at', 'ant', 'avit', 'erunt', 'am', 'em', 'ae', 'ret', 'set',
  'o', 'i', 'a', 'e', 'ur', 'tur', 'bus', 'que', 'an', 'ui', 'tor', 'tio', 'ix', 'ex'
];

const FRENCH_EXCLUSIVE_SUFFIXES = [
  'ment', 'esse', 'aire', 'euse', 'eux', 'eaux', 'eau', 'tion', 'sion',
  'able', 'ible', 'ique', 'ance', 'ence', 'isme', 'iste', 'oir', 'oire'
];

function isLatinLemma(phrase) {
  if (!phrase) return false;
  // Accents exclusifs au français (le tréma ë / ï est fréquent dans la typographie de la Vulgate : Israël, Ephraïm, Noë...)
  if (/[éèêàâîôûç]/i.test(phrase)) return false;

  const clean = phrase.replace(/[\.\,\:\;\(\)\«\»\—\-]/g, ' ').trim();
  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length === 0) return false;
  
  if (words.length === 1 && /^(?:[ivxlcdm]+|\d+°?)$/i.test(words[0])) return false;
  
  // Si commence par une préposition française ou mot de liaison suivi de mots capitalisés
  const firstWord = words[0].toLowerCase();
  if (['du', 'des', 'le', 'la', 'les', 'un', 'une', 'par', 'pour', 'dans', 'sur', 'sous', 'vers', 'chez'].includes(firstWord)) {
    return false;
  }
  if (firstWord === 'de') {
    const secondWord = words[1] ? words[1].toLowerCase() : '';
    if (['la', 'le', 'les', 'l', 'ce', 'cette', 'ces', 'mon', 'son', 'sa', 'notre', 'votre', 'leur'].includes(secondWord)) {
      return false;
    }
  }

  // Exclusion formelle des mots portant des suffixes morphologiques exclusivement français
  for (const w of words) {
    const lw = w.toLowerCase();
    if (lw !== 'esse' && FRENCH_EXCLUSIVE_SUFFIXES.some(suf => lw.length > suf.length + 2 && lw.endsWith(suf))) {
      return false;
    }
  }

  // Vérifier qu'aucun mot vide français exclusif n'est présent
  let frCount = 0;
  for (const w of words) {
    const lw = w.toLowerCase();
    if (LATIN_COMMON_WORDS.has(lw)) continue;
    if (FRENCH_STOP_WORDS.has(lw)) {
      frCount++;
    }
  }
  if (frCount > 0) return false;

  // Validation positive latine : au moins la moitié des mots doivent porter une marque ou un lemme latin
  let latCount = 0;
  for (const w of words) {
    const lw = w.toLowerCase().replace(/æ/g, 'ae').replace(/œ/g, 'oe');
    if (LATIN_STOP_WORDS.has(lw) || LATIN_COMMON_WORDS.has(lw)) {
      latCount++;
    } else if (LATIN_ENDINGS.some(end => lw.endsWith(end))) {
      latCount++;
    }
  }
  return latCount >= Math.max(1, Math.floor(words.length / 2));
}

const ATLAS_ARCHEO_REGEX = /(?:Atl\.|Atlas|atl\.)\s*(?:arch[ée]ol(?:ogique)?|arch)\.?(?:\s+de\s+la\s+Bible)?,?\s*(?:p[li]\.|\s+pl\.\s*|\s*pl\.)\s*([ivxlcdm\d]+)(?:,\s*(?:fig|filg)\.?,?\s*[\d\s,\-–]+(?:\s+et\s+\d+)?)?(?:\s*;\s*(?:p[li]\.|\s+pl\.\s*|\s*pl\.)\s*([ivxlcdm\d]+)(?:,\s*(?:fig|filg)\.?,?\s*[\d\s,\-–]+(?:\s+et\s+\d+)?)?){0,2}/gi;
const ATLAS_GEO_REGEX = /(?:Atl\.|Atlas|atl\.)\s*(?:g[ée]ogr(?:aphique)?|g[ée]og)\.?(?:\s+de\s+la\s+Bible)?,?\s*(?:p[li]\.|\s+pl\.\s*|\s*pl\.)\s*([ivxlc\d]+)/gi;
const ATLAS_HN_REGEX = /(?:Atl\.|Atlas|atl\.)\s*(?:d['’]hist(?:oire)?\s+nat(?:urelle)?|hist(?:oire)?\s+nat(?:urelle)?|HN|hn)\.?(?:\s+de\s+la\s+Bible)?,?\s*(?:p[li]\.|\s+pl\.\s*|\s*pl\.)\s*([ivxlcdm\d]+)(?:,\s*(?:fig|filg)\.?,?\s*[\d\s,\-–]+(?:\s+et\s+\d+)?)?(?:\s*;\s*(?:p[li]\.|\s+pl\.\s*|\s*pl\.)\s*([ivxlcdm\d]+)(?:,\s*(?:fig|filg)\.?,?\s*[\d\s,\-–]+(?:\s+et\s+\d+)?)?){0,2}/gi;

function atlasRomanToInt(r) {
  const map = { i: 1, v: 5, x: 10, l: 50, c: 100 };
  const s = r.toLowerCase();
  let total = 0;
  for (let i = 0; i < s.length; i++) {
    const v = map[s[i]];
    if (!v) return 0;
    const next = map[s[i + 1]] || 0;
    total += v < next ? -v : v;
  }
  return total;
}

// Homoglyphes latins fréquents dans l'OCR des mots grecs (Tomes 6-8) : xλητoĩ → κλητοῖ
const GREEK_HOMOGLYPHS = {
  a: 'α', e: 'ε', é: 'έ', è: 'ὲ', i: 'ι', í: 'ί', ĩ: 'ῖ', o: 'ο', ó: 'ό', u: 'υ', ú: 'ύ',
  v: 'ν', x: 'κ', p: 'ρ', c: 'ς', ç: 'ς', t: 'τ', y: 'γ', k: 'κ', n: 'η', w: 'ω', s: 'σ',
};
function normalizeGreekHomoglyphs(text) {
  return text.replace(/[A-Za-zÀ-ÿĩ\u0370-\u03FF\u1F00-\u1FFF]+/g, (tok) => {
    if (!/[\u0370-\u03FF\u1F00-\u1FFF]/.test(tok) || !/[A-Za-zÀ-ÿĩ]/.test(tok)) return tok;
    let out = '';
    for (const ch of tok) out += GREEK_HOMOGLYPHS[ch] || ch;
    if (/[A-Za-zÀ-ÿ]/.test(out)) return tok; // lettre non mappable : ne pas risquer
    return out.replace(/σ$/, 'ς');
  });
}

function renderInlineContent(text) {
  if (text) text = normalizeGreekHomoglyphs(text);
  if (!text) return null;

  // Nettoyage préalable d'éventuelles balises Link/a orphelines ou imbriquées injectées historiquement en base
  text = text.replace(/<Link\s+to=["'][^"']*["']>(?:<Link\s+to=["'][^"']*["']>)?(.*?)(?:<\/Link>){1,2}/gi, '$1');
  // Sanctuarisation des astérisques asymétriques (*texte** ou **texte*) en gras propre
  text = text
    .replace(/(?<!\*)\*([^*\s\n][^*\n]*?)\*\*(?!\*)/g, '**$1**')
    .replace(/(?<!\*)\*\*([^*\s\n][^*\n]*?)\*(?!\*)/g, (match, p1) => {
      if (/^\d+\.\s*$/.test(p1)) return match;
      return `**${p1}**`;
    });

  // --- Pre-pass 0 : renvois à l'Atlas d'histoire naturelle (« Atl. d'hist. nat., pl. V, fig. 2 ; pl. VI, fig. 3 »)
  if (text.search(ATLAS_HN_REGEX) !== -1) {
    const nodes = [];
    let last = 0;
    let key = 0;
    const re = new RegExp(ATLAS_HN_REGEX.source, 'gi');
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) nodes.push(<span key={'hn-t-' + key++}>{renderInlineContent(text.substring(last, m.index))}</span>);
      const fullMatchedText = m[0];
      const subRefs = fullMatchedText.split(/\s*;\s*(?=(?:p[li]\.|\s+pl\.\s*|\s*pl\.))/i);
      subRefs.forEach((subText, subIdx) => {
        if (subIdx > 0) {
          nodes.push(<span key={'hn-sep-' + key++}> ; </span>);
        }
        const pm = subText.match(/(?:p[li]\.|\s+pl\.\s*|\s*pl\.)\s*([ivxlcdm\d]+)/i);
        const fm = subText.match(/(?:fig|filg)\.?,?\s*([\d\s,\-–]+(?:\s+et\s+\d+)?)/i);
        const rawPlate = pm ? pm[1] : '';
        const rawFig = fm ? fm[1].trim() : '';
        const n = parseInt(rawPlate, 10) || atlasRomanToInt(rawPlate);
        if (n >= 1 && n <= 112) {
          const figQuery = rawFig ? '&fig=' + encodeURIComponent(rawFig) : '';
          const returnUrl = typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : '/bible';
          nodes.push(
            <Link
              key={'hn-l-' + key++}
              to={'/histoire-naturelle?planche=' + n + figQuery + '&from_bible=1'}
              state={{ fromBible: true, returnUrl }}
              className="text-emerald-700 dark:text-emerald-400 hover:underline font-semibold mr-0.5"
              title={'Ouvrir la planche ' + n + " de l'Atlas d'histoire naturelle"}
            >
              {subText}
            </Link>
          );
        } else {
          nodes.push(<span key={'hn-p-' + key++}>{subText}</span>);
        }
      });
      last = m.index + m[0].length;
    }
    if (last < text.length) nodes.push(<span key={'hn-t-' + key++}>{renderInlineContent(text.substring(last))}</span>);
    return nodes;
  }

  // --- Pre-pass 1 : renvois à l'Atlas géographique (« Atl. géogr., pl. XVI »)
  if (text.search(ATLAS_GEO_REGEX) !== -1) {
    const nodes = [];
    let last = 0;
    let key = 0;
    const re = new RegExp(ATLAS_GEO_REGEX.source, 'gi');
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) nodes.push(<span key={'geo-t-' + key++}>{renderInlineContent(text.substring(last, m.index))}</span>);
      const matchedText = m[0];
      const pm = matchedText.match(/(?:p[li]\.|\s+pl\.\s*|\s*pl\.)\s*([ivxlc\d]+)/i);
      const n = pm ? (parseInt(pm[1], 10) || romanToInt(pm[1])) : 0;
      if (n >= 1) {
        const returnUrl = typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : '/bible';
        nodes.push(
          <Link
            key={'geo-l-' + key++}
            to={`/cartes?planche=${n}&from_bible=1`}
            state={{ fromBible: true, returnUrl }}
            className="text-emerald-700 dark:text-emerald-400 hover:underline font-semibold mr-0.5"
            title={`Ouvrir la planche ${n} de l'Atlas géographique`}
          >
            {matchedText}
          </Link>
        );
      } else {
        nodes.push(<span key={'geo-p-' + key++}>{matchedText}</span>);
      }
      last = m.index + m[0].length;
    }
    if (last < text.length) nodes.push(<span key={'geo-t-' + key++}>{renderInlineContent(text.substring(last))}</span>);
    return nodes;
  }

  // --- Pre-pass 2 : renvois à l'Atlas archéologique (« Atl. archéol., pl. XLV, fig. 4 »)
  if (text.search(ATLAS_ARCHEO_REGEX) !== -1) {
    const nodes = [];
    let last = 0;
    let key = 0;
    const re = new RegExp(ATLAS_ARCHEO_REGEX.source, 'gi');
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) nodes.push(<span key={'at-t-' + key++}>{renderInlineContent(text.substring(last, m.index))}</span>);
      const fullMatchedText = m[0];
      
      // Si la correspondance regroupe deux planches séparées par un point-virgule (ex: "Atlas archéologique, pl. XX, fig. 12 ; pl. XLII, fig. 15")
      // on découpe pour créer deux liens distincts et précis !
      const subRefs = fullMatchedText.split(/\s*;\s*(?=(?:p[li]\.|\s+pl\.\s*|\s*pl\.))/i);
      
      subRefs.forEach((subText, subIdx) => {
        if (subIdx > 0) {
          nodes.push(<span key={'at-sep-' + key++}> ; </span>);
        }
        const pm = subText.match(/(?:p[li]\.|\s+pl\.\s*|\s*pl\.)\s*([ivxlcdm\d]+)/i);
        const fm = subText.match(/(?:fig|filg)\.?,?\s*([\d\s,\-–]+(?:\s+et\s+\d+)?)/i);
        const rawPlate = pm ? pm[1] : '';
        const rawFig = fm ? fm[1].trim() : '';
        const contextSnippet = text.substring(Math.max(0, m.index - 150), Math.min(text.length, m.index + m[0].length + 150));
        const resolved = resolveAtlasArcheo(rawPlate, rawFig, contextSnippet);

        if (resolved && resolved.plate >= 1) {
          const figQuery = resolved.fig ? `&fig=${encodeURIComponent(resolved.fig)}` : '';
          const origQuery = resolved.isConcordance ? `&orig_pl=${encodeURIComponent(resolved.originalRoman || '')}&orig_fig=${encodeURIComponent(rawFig || '')}` : '';
          const fromBibleQuery = '&from_bible=1';
          const returnUrl = typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : '/bible';
          nodes.push(
            <Link
              key={'at-l-' + key++}
              to={`/gravures?planche=${resolved.plate}${figQuery}${origQuery}${fromBibleQuery}`}
              state={{ fromBible: true, returnUrl }}
              className="text-rubric-red dark:text-red-400 hover:underline font-semibold mr-1 inline-flex items-center flex-wrap gap-1 align-baseline"
              title={resolved.note || `Ouvrir la planche ${resolved.roman} de l'Atlas archéologique`}
            >
              <span>{subText}</span>
              {resolved.isConcordance && (
                <span className="text-[11px] font-sans font-medium text-amber-800 dark:text-amber-200 bg-amber-100/90 dark:bg-amber-950/70 border border-amber-300 dark:border-amber-700 px-1.5 py-0.2 rounded inline-flex items-center gap-0.5 no-underline shadow-2xs">
                  [→ éd. 1883 : pl. {resolved.roman}{resolved.fig ? `, fig. ${resolved.fig}` : ''}]
                </span>
              )}
            </Link>
          );
        } else {
          nodes.push(<span key={'at-p-' + key++}>{subText}</span>);
        }
      });
      last = m.index + m[0].length;
    }
    if (last < text.length) nodes.push(<span key={'at-t-' + key++}>{renderInlineContent(text.substring(last))}</span>);
    return nodes;
  }

  // --- Pre-pass: extract raw HTML <span> blocks from the DB text (amber Hebrew terms, etc.)
  // Split the text into alternating plain-text / html-span segments.
  const htmlSpanRegex = /(<span\s[^>]*>.*?<\/span>)/g;
  const htmlTokens = [];
  let lastHtmlIdx = 0;
  let htmlMatch;
  while ((htmlMatch = htmlSpanRegex.exec(text)) !== null) {
    if (htmlMatch.index > lastHtmlIdx) {
      htmlTokens.push({ type: 'text', value: text.substring(lastHtmlIdx, htmlMatch.index) });
    }
    htmlTokens.push({ type: 'html', value: htmlMatch[1] });
    lastHtmlIdx = htmlSpanRegex.lastIndex;
  }
  if (lastHtmlIdx < text.length) {
    htmlTokens.push({ type: 'text', value: text.substring(lastHtmlIdx) });
  }

  // If there are HTML spans, render each token appropriately and combine.
  if (htmlTokens.some(t => t.type === 'html')) {
    return htmlTokens.map((token, i) => {
      if (token.type === 'html') {
        let htmlVal = token.value;
        // Règle 69 : Sanctuariser le gras rubriqué sur les lemmes latins dans les balises span
        if (htmlVal.includes('text-rubric-red') && htmlVal.includes('font-weight: normal')) {
          htmlVal = htmlVal.replace(/font-weight:\s*normal;?/g, 'font-weight: 600;');
          if (!htmlVal.includes('font-semibold')) {
            htmlVal = htmlVal.replace(/class="([^"]*)"/, 'class="$1 font-semibold"');
          }
        }
        return <span key={'raw-html-' + i} dangerouslySetInnerHTML={{ __html: htmlVal }} />;
      }
      // Recursively process plain-text segments with the normal pipeline.
      return <span key={'plain-' + i}>{renderInlineContent(token.value)}</span>;
    });
  }
  // No HTML spans — fall through to the normal regex pipeline below.

  const regex = /([\u0370-\u03FF\u1F00-\u1FFF]+(?:[\s,·]+[\u0370-\u03FF\u1F00-\u1FFF]+)*)|([\u0590-\u05FF]+(?:[\s\u0590-\u05FF]+)*)|([\u2160-\u217F]+|\b(?:[ivxlcdm]+|[IVXLCDM]+)\b(?=\s*[,–—\-:]\s*\d))|(\b(?:in expiationem|de bobus|de pecoribus|pro delicto|pro peccato|hostiam|tâmîm|qorbân|b'hêmah|bâqâr|s'ôn|'ôlah|ad placandum|suavem odorem Domino|ad ostium tabernaculi|per altaris circuitum|panem Dei sui|sanctum sanctorum|lex carnalis|ex opere operato|in odorem suavitatis)\b)|(\*\*([^*]+)\*\*|__([^_]+)__)|(\*([^*]+)\*|_([^_]+)_)|(\b(?:Man\. bibl\.|Dict\. de la Bible|Ant\. jud\.|Guerre des Juifs|De Civ\. Dei|Targ\.|Talm\.|Sept\.|LXX|Vulg\.|Pesh\.|M\. T\.|Hebr\.)(?!\w))|(\[([^\]]+)\])/gi;


  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    if (match[1]) {
      // Grec Unicode
      parts.push(
        <span
          key={'gk-' + match.index}
          className="font-serif italic font-medium text-emerald-800 dark:text-emerald-300 tracking-normal"
          title="Texte grec (Septante / Nouveau Testament)"
        >
          {match[1]}
        </span>
      );
    } else if (match[2]) {
      // Hébreu Unicode
      parts.push(
        <span
          key={'hb-' + match.index}
          dir="rtl"
          className="font-serif font-semibold text-amber-900 dark:text-amber-200 text-[1.05em]"
          title="Texte hébreu biblique"
        >
          {match[2]}
        </span>
      );
    } else if (match[3]) {
      // Chiffre romain Unicode ou typographique Fillion (ex: ⅰ, ⅱ, Ⅰ, Ⅱ, i, ii)
      parts.push(
        <span
          key={'rom-' + match.index}
          className="font-serif font-bold text-rubric-red dark:text-red-300 uppercase tracking-wider text-[0.9em] inline"
          style={{ fontVariant: 'small-caps' }}
          title="Chiffre romain (Fillion)"
        >
          {match[3]}
        </span>
      );
    } else if (match[4]) {
      // Expression latine reconnue avec traduction française
      const trans = findTranslation(match[4]);
      parts.push(
        <span key={'lat-' + match.index} className="inline">
          <span
            className="font-serif italic font-bold text-rubric-red dark:text-red-300 tracking-wide"
            style={{ fontStyle: 'italic', fontWeight: '700' }}
            title="Terme latin de la Vulgate"
          >
            {match[4]}
          </span>
          {trans && (
            <span className="font-body italic text-ink-secondary/90 dark:text-neutral-400 text-[0.93em] ml-1" title="Traduction de Fillion">
              [<em>{trans}</em>]
            </span>
          )}
        </span>
      );
    } else if (match[5]) {
      // Texte balisé en GRAS Markdown : **mot** ou __mot__
      const boldText = (match[6] || match[7] || '').trim();
      if (isLatinLemma(boldText)) {
        parts.push(
          <em
            key={'b-lat-' + match.index}
            className="font-serif italic font-bold text-rubric-red dark:text-red-300 tracking-wide inline"
            style={{ fontStyle: 'italic', fontWeight: '700' }}
            title="Terme latin de la Vulgate"
          >
            {boldText}
          </em>
        );
      } else {
        parts.push(
          <strong key={'b-' + match.index} className="font-semibold text-rubric-red dark:text-red-400">
            {boldText}
          </strong>
        );
      }
        } else if (match[11]) {
        const abbr = match[11];
        // Si l'abréviation est 'Hebr.' ou 'Hébr.' et qu'elle est immédiatement suivie d'une référence de chapitre/verset
        // (ex: Hebr. ix, 13 ; Hébr. XI, 4), il s'agit de l'Épître aux Hébreux (le livre) et non du "Texte Hébreu".
        const postText = text.substring(match.index + abbr.length);
        const isEpistleReference = /^hebr\.?$/i.test(abbr) && /^\s*[ivxlcdm\d]+/i.test(postText);

        if (isEpistleReference) {
          parts.push(abbr);
        } else {
          const full = ABBREVIATIONS[abbr] || abbr;
          parts.push(
            <CustomTooltip key={'abbr-' + match.index} triggerText={abbr} fullText={full} />
          );
        }
    } else if (match[12]) {
      // Traduction française ou glose entre crochets [...] — italique fin
      // Si la partie précédente immédiate se terminait par un lemme latin non balisé, on le transforme en gras rubriqué
      const bracketContent = match[13] || '';
      const hasHebrewOrGreek = /[\u0590-\u05FF\u0370-\u03FF\u1F00-\u1FFF]/.test(bracketContent);
      let prefixVers = null;
      if (!hasHebrewOrGreek && parts.length > 0 && typeof parts[parts.length - 1] === 'string') {
        const lastStr = parts[parts.length - 1];
        // Ne pas déclencher si le dernier mot contient un caractère non latin / translittéré (ex: 'ašérah)
        const lastWord = (lastStr.trim().split(/\s+/).pop() || '');
        const isCleanAscii = /^['a-zA-Z\u00C6\u00E6\u0152\u0153\u00CB\u00EB\u00CF\u00EF\.\,\:\;\(\)\—\-]+$/.test(lastWord);

        if (isCleanAscii) {
          // Vérifier si précédé par un éventuel (vers. N)
          const versM = lastStr.match(/(?:^|\s)(\(vers\.\s*\d+\)\.?)\s*$/i);
          let checkStr = lastStr;
          if (versM) {
            prefixVers = versM[1];
            checkStr = lastStr.substring(0, lastStr.length - versM[0].length);
          }

          const candidateM = checkStr.match(/((?:[a-zA-Z\u00C6\u00E6\u0152\u0153\u00CB\u00EB\u00CF\u00EF]+(?:\'[a-zA-Z]+)?(?:\.\.\.)?|\.\.\.)(?:[\s,]+(?:[a-zA-Z\u00C6\u00E6\u0152\u0153\u00CB\u00EB\u00CF\u00EF]+(?:\'[a-zA-Z]+)?(?:\.\.\.)?|\.\.\.))*)\s*$/);
          if (candidateM) {
            const rawMatched = candidateM[1];
            const wordTokens = rawMatched.trim().split(/\s+/).filter(Boolean);
            let foundLemma = null;
            let beforePrefix = '';
            for (let i = 0; i < wordTokens.length; i++) {
              const subWords = wordTokens.slice(i);
              const subCandidate = subWords.join(' ').replace(/^[\s,]+|[\s,]+$/g, '');
              if (isLatinLemma(subCandidate) && subCandidate.length >= 3) {
                const idx = checkStr.lastIndexOf(subWords[0]);
                foundLemma = subCandidate;
                beforePrefix = checkStr.substring(0, idx);
                break;
              }
            }

            if (foundLemma) {
              // Remplacer la chaîne de fin par le préfixe restant
              if (beforePrefix) {
                parts[parts.length - 1] = beforePrefix;
              } else {
                parts.pop();
              }
              // Insérer le lemme latin en gras rubriqué
              parts.push(
                <span
                  key={'inline-lat-' + match.index}
                  className="font-serif italic font-bold text-rubric-red dark:text-red-400 tracking-wide mr-1 inline"
                  style={{ fontStyle: 'italic', fontWeight: '700' }}
                  title="Lemme latin de la Vulgate"
                >
                  {foundLemma}
                </span>
              );
              if (prefixVers) {
                parts.push(
                  <span key={'inline-vref-' + match.index} className="text-rubric-red/80 font-semibold text-[0.9em] mr-1 inline">
                    {prefixVers}
                  </span>
                );
              }
            }
          }
        }
      }

      parts.push(
        <em
          key={'br-' + match.index}
          className="font-body italic font-light text-ink-secondary/90 dark:text-neutral-400 text-[0.93em]"
          title="Traduction de Fillion"
        >
          [{renderInlineContent(bracketContent)}]
        </em>
      );
    } else if (match[8]) {
      // Texte balisé en italique Markdown : *mot* ou _mot_
      const innerText = (match[9] || match[10] || '').trim();
      if (!innerText) {
        lastIndex = regex.lastIndex;
        continue;
      }
      
      const abbrKey = Object.keys(ABBREVIATIONS).find(k => k.toLowerCase() === innerText.toLowerCase());
      if (abbrKey) {
        const full = ABBREVIATIONS[abbrKey];
        parts.push(
          <CustomTooltip key={'abbr-ast-' + match.index} triggerText={innerText} fullText={full} />
        );
        lastIndex = regex.lastIndex;
        continue;
      }

      const trans = findTranslation(innerText);
      const beforeSnippet = text.substring(Math.max(0, match.index - 40), match.index);
      const isHeb = isHebrewTerm(innerText, beforeSnippet);
      const isLatin = isLatinLemma(innerText);
      
      // Ne pas afficher la traduction entre crochets si elle figure déjà immédiatement après
      let showTrans = false;
      if (trans) {
        const restSnippet = text.substring(match.index + match[0].length);
        showTrans = !isTranslationRedundant(trans, restSnippet);
      }

      if (isHeb) {
        // Terme hébreu translittéré (ex: bara', 'āśāh, yaṣar, banah, 'Elohim, tôhu, higgîd, etc.) : DROIT (NON-ITALIQUE) AMBRE/OR
        parts.push(
          <span key={'ast-hb-' + match.index} className="inline">
            <span
              className="font-serif not-italic font-bold text-amber-900 dark:text-amber-200 bg-amber-50/70 dark:bg-amber-950/40 px-1 py-0.5 rounded tracking-wide inline"
              style={{ fontStyle: 'normal' }}
              title="Terme hébreu ou oriental"
            >
              {innerText}
            </span>
            {showTrans && (
              <span className="font-body italic text-ink-secondary/90 dark:text-neutral-400 text-[0.93em] ml-1" title="Traduction de Fillion">
                [<em>{trans}</em>]
              </span>
            )}
          </span>
        );
      } else if (isLatin || trans) {
        // Citation ou lemme latin de la Vulgate : ITALIQUE ROUGE RUBRIQUE
        const restSnippet = text.substring(match.index + match[0].length);
        let inlineParenNode = null;
        let skipLen = 0;
        if (!showTrans) {
          const pm = restSnippet.match(/^\s*([\(\[][^\)\]]+[\)\]])/);
          if (pm) {
            inlineParenNode = (
              <em key={'paren-' + match.index} className="font-body italic text-ink-secondary/90 dark:text-neutral-400 text-[0.93em] ml-1 mr-1.5 inline" title="Traduction de Fillion">
                {pm[1]}
              </em>
            );
            skipLen = pm[0].length;
          }
        }

        parts.push(
          <span key={'ast-lat-' + match.index} className="inline">
            <em
              className="font-serif italic font-bold text-rubric-red dark:text-red-300 tracking-wide inline"
              style={{ fontStyle: 'italic', fontWeight: '700' }}
              title="Terme latin de la Vulgate"
            >
              {innerText}
            </em>
            {showTrans && (
              <span className="font-body italic text-ink-secondary/90 dark:text-neutral-400 text-[0.93em] ml-1 mr-1.5" title="Traduction de Fillion">
                [<em>{trans}</em>]
              </span>
            )}
            {inlineParenNode}
          </span>
        );
        if (skipLen > 0) {
          regex.lastIndex += skipLen;
        }
      } else {
        // Italique général
        parts.push(
          <em key={'ast-em-' + match.index} className="font-body italic text-ink-primary dark:text-neutral-200" style={{ fontStyle: 'italic' }}>
            {innerText}
          </em>
        );
      }
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
}

/**
 * Traitement d'un segment de commentaire séparé par un tiret cadratin ' — '.
 */
function renderSegment(segment, index) {
  let trimmed = segment.trim();
  
  // Titres Markdown ##, ### et ####
  if (trimmed.startsWith('#### ')) {
    return (
      <h4 key={'h4-' + index} className="font-heading font-semibold text-rubric-red dark:text-red-400 tracking-wide text-sm sm:text-base my-2 pb-0.5 border-b border-rubric-red/15">
        {renderInlineContent(trimmed.substring(5))}
      </h4>
    );
  }
  if (trimmed.startsWith('### ')) {
    return (
      <h3 key={'h3-' + index} className="font-heading font-bold text-rubric-red dark:text-red-300 tracking-wider uppercase text-sm sm:text-base my-3 pb-1 border-b-2 border-rubric-red/30">
        {renderInlineContent(trimmed.substring(4))}
      </h3>
    );
  }
  if (trimmed.startsWith('## ')) {
    return (
      <h2 key={'h2-' + index} className="font-heading font-bold text-rubric-red dark:text-red-300 tracking-widest uppercase text-base sm:text-lg my-4 pb-1.5 border-b-2 border-rubric-red/40 text-center">
        {renderInlineContent(trimmed.substring(3))}
      </h2>
    );
  }

  // Gravure / Planche d'illustration Markdown (ex: ![Légende](/illustrations_fillion/tome1_p142_gravure.jpg))
  const imgMatch = trimmed.match(/^!\[(.*?)\]\((.*?)\)$/);
  if (imgMatch) {
    const caption = imgMatch[1];
    const src = imgMatch[2];
    return (
      <figure key={'img-' + index} className="my-4 rounded-lg overflow-hidden border border-border bg-paper-dark/40 p-2 sm:p-3 text-center shadow-sm">
        <a href={src} target="_blank" rel="noopener noreferrer" title="Cliquer pour agrandir l'illustration">
          <img
            src={src}
            alt={caption || "Illustration de la Bible Fillion"}
            className="w-full max-h-[520px] object-contain mx-auto rounded shadow transition hover:opacity-95 cursor-zoom-in bg-black/5"
            loading="lazy"
          />
        </a>
        {caption && (
          <figcaption className="mt-2 text-xs sm:text-sm font-serif italic text-ink-secondary">
            ✦ {caption} ✦
          </figcaption>
        )}
      </figure>
    );
  }

  // Titres de grandes divisions / parties & sous-titres d'ensemble (ex: "LE DIVIN PRÉLUDE", "LA CRÉATION. I, 1 — II, 3.")
  if (/^(?:PREMIÈRE|DEUXIÈME|TROISIÈME|QUATRIÈME|CINQUIÈME)\s+PARTIE/i.test(trimmed) ||
      /^SECTION\s+[IVXLCDM\u2160-\u217F]+/i.test(trimmed) ||
      /^[A-ZÀÂÉÈÊËÎÏÔÖÙÛÜÇ\u2160-\u217F\s]{4,}$/.test(trimmed) ||
      /^[A-ZÀÂÉÈÊËÎÏÔÖÙÛÜÇ0-9°\s\.\,\'\-–—\u2160-\u217F]+\.\s+[IVXLCDM\u2160-\u217F\d\s\.\,\-–—]+$/.test(trimmed)) {
    return (
      <div key={'head-' + index} className="font-heading font-bold text-rubric-red dark:text-red-300 tracking-wide uppercase text-sm sm:text-base my-2.5 pb-1 border-b border-rubric-red/30">
        {renderInlineContent(trimmed)}
      </div>
    );
  }

  // Nettoyage préalable d'un éventuel tiret cadratin ou tiret au tout début du segment
  trimmed = trimmed.replace(/^[—–-]\s*/, '');

  // Sous-divisions de plan (ex: 'I. — Les holocaustes.', '1° Holocaustes...', '1. Ce verset...')
  // Ne pas capturer si c'est un numéro de verset suivi d'un astérisque ou d'un lemme latin (ex: '2. *Terra autem*')
  const planMatch = trimmed.match(/^((?:[IVXLCDM\u2160-\u217F]+\.|\d+°|\d+\.|\d+\s*—\s*\d+[a-z]?\.?|Chap\.\s+[IVXLCDM\u2160-\u217F\d]+\.))\s*(.*)/i);
  if (planMatch && !/^\d+\.\s*\*/.test(trimmed) && (!isLatinLemma(planMatch[1]) || /^(?:Chap|\d+|[IVXLCDM\u2160-\u217F]+\.)/i.test(planMatch[1]))) {
    return (
      <span key={'plan-' + index}>
        {index > 0 && <span className="text-rubric-red/50 mx-1.5 select-none font-sans font-bold">—</span>}
        <span className="font-heading font-semibold text-rubric-red dark:text-red-400 mr-1">
          {planMatch[1]}
        </span>
        {planMatch[2] && renderInlineContent(planMatch[2])}
      </span>
    );
  }

  // Lemmes latins de la Vulgate en tête de segment (balisés explicitement en italique/astérisques ou connus dans le lexique)
  let prefixNum = '';
  let rawLemmaCandidate = null;
  let rawPunctuation = '';
  let restText = '';

  // Détection du lemme latin étoilé (*lemme* ou **lemme**), avec support d'un préfixe de verset simple ou plage (ex: '**1-2.** *Accesserunt*' ou '1-2. *Accesserunt*' ou '2. *Terra autem*')
  const astMatch = trimmed.match(/^((?:\*\*\d+(?:-\d+)?\.\*\*\s*|\d+(?:-\d+)?\.\s*)?)(?:\*{1,2})([^*]+)(?:\*{1,2})([.,;:!?]*|\.\.\.|\s*—|\s*$)\s*(.*)/);
  if (astMatch) {
    prefixNum = astMatch[1] || '';
    rawLemmaCandidate = astMatch[2].trim();
    rawPunctuation = astMatch[3] || '';
    restText = astMatch[4] || '';
  } else {
    // Si pas d'astérisques, détection d'un lemme latin de début de segment (avec ligatures æ/œ/ë/ï, ellipses et support de plage '1-2.')
    const plainMatch = trimmed.match(/^((?:\*\*\d+(?:-\d+)?\.\*\*\s*|\d+(?:-\d+)?\.\s*)?)([a-zA-Z\u00C6\u00E6\u0152\u0153\u00CB\u00EB\u00CF\u00EF\'-]+(?:\s+[a-zA-Z\u00C6\u00E6\u0152\u0153\u00CB\u00EB\u00CF\u00EF\'-]+|\s*\.\.\.\s*[a-zA-Z\u00C6\u00E6\u0152\u0153\u00CB\u00EB\u00CF\u00EF\'-]+|\s*\.\.\.)*)([.,;:!?]*|\.\.\.|\s*—|\s*$)(\s*(?:\[|\(vers\.|—|:|;|\.\.\.|\.[A-ZÀ-ÖØ-Þ\s]).*)$/i);
    if (plainMatch && isLatinLemma(plainMatch[2])) {
      prefixNum = plainMatch[1] || '';
      rawLemmaCandidate = plainMatch[2].trim();
      rawPunctuation = plainMatch[3] || '';
      restText = plainMatch[4] || '';
    } else {
      // Fallback avec dictionnaire fixe si pas suivi directement par [ ou (vers. N)
      const dictMatch = trimmed.match(/^((?:\*\*\d+(?:-\d+)?\.\*\*\s*|\d+(?:-\d+)?\.\s*)?)([A-Z\u00C6\u0152][a-zA-Z\u00C6\u00E6\u0152\u0153\u00CB\u00EB\u00CF\u00EF',]*(?:\s+[a-zA-Z\u00C6\u00E6\u0152\u0153\u00CB\u00EB\u00CF\u00EF',]+)*(?:\.\.\.|\s*\.\.\.\s*[A-Z\u00C6\u0152]?[a-zA-Z\u00C6\u00E6\u0152\u0153\u00CB\u00EB\u00CF\u00EF',]+)*)([.,;:!?]*|\.\.\.|\s*—|\s*$)\s*(.*)/);
      if (dictMatch && findTranslation(dictMatch[2])) {
        prefixNum = dictMatch[1] || '';
        rawLemmaCandidate = dictMatch[2].trim();
        rawPunctuation = dictMatch[3] || '';
        restText = dictMatch[4] || '';
      }
    }
  }

  // Lemme latin terminé par « ... » ou « . » suivi d'une phrase française (ex: « His qui... Les destinataires » ou « A diebus Asor-Haddan. Asarhaddon... »)
  if (!rawLemmaCandidate) {
    const sentMatch = trimmed.match(/^((?:\*\*\d+(?:-\d+)?\.\*\*\s*|\d+(?:-\d+)?\.\s*)?)([A-Z\u00C6\u0152][a-zA-Z\u00C6\u00E6\u0152\u0153\u00CB\u00EB\u00CF\u00EF',.\s\-]{0,110}?[a-zA-Z\u00E6\u0153\u00EB\u00EF])(\s*\.{1,3})\s+(?=[A-ZÀ-ÖØ-Þ«])([\s\S]*)$/);
    if (sentMatch) {
      const cand = sentMatch[2].trim();
      const punct = sentMatch[3].trim();
      const words = cand.replace(/[.,]/g, ' ').split(/\s+/).filter(Boolean);
      const first = words[0] ? words[0].toLowerCase() : '';
      const NON_LEMMA_STARTS = new Set(['voyez', 'cf', 'comp', 'hebr', 'saint', 'notez', 'selon', 'allusion', 'littéralement', 'grec', 'mieux', 'ici', 'comparez', 'remarquez', 'voir']);
      const latinEnding = /(?:us|um|is|ae|æ|it|unt|ant|ent|am|em|os|as|es|o|i|a|e|ur|tur|bus|que|orum|arum|ibus|an)$/i;
      const hasEllipsis = punct.length >= 2 || /\.\./.test(cand);
      if (words.length >= 1 && words.length <= 8 && !NON_LEMMA_STARTS.has(first) &&
          isLatinLemma(cand) && (hasEllipsis || (words.length <= 5 && latinEnding.test(words[words.length - 1])))) {
        prefixNum = sentMatch[1] || '';
        rawLemmaCandidate = cand;
        rawPunctuation = punct;
        restText = ' ' + (sentMatch[4] || '');
      }
    }
  }

  if (rawLemmaCandidate && isLatinLemma(rawLemmaCandidate)) {
    const lemmaText = rawLemmaCandidate + rawPunctuation;
    const translation = findTranslation(rawLemmaCandidate);
    const showTrans = translation && !isTranslationRedundant(translation, restText);

    let parenTransNode = null;
    let newRestText = restText;
    if (!showTrans) {
      // 1. Capture d'un éventuel repère de verset optionnel (vers. N) suivi immédiatement d'une traduction entre crochets ou parenthèses
      const fullMatch = restText.match(/^(\s*\(vers\.\s*\d+\)\.?\s*)?(\[[^\]]+\]|\([^\)]+\))(\s*.*)/i);
      if (fullMatch) {
        const prefixVers = fullMatch[1] ? fullMatch[1].trim() : '';
        const transBracket = fullMatch[2];
        newRestText = fullMatch[3] || '';
        parenTransNode = (
          <span className="inline">
            {prefixVers && (
              <span className="text-rubric-red/80 font-semibold text-[0.9em] mr-1.5">{prefixVers}</span>
            )}
            <em className="font-body italic text-ink-secondary/90 dark:text-neutral-400 text-[0.93em] mr-1.5 inline" title="Traduction de Fillion">
              {transBracket}
            </em>
          </span>
        );
      } else {
        // 2. Détection d'une glose explicative ou traduction introduite par deux-points (ex: ': les chefs de mille.' ou ': dans les parcs de ses troupeaux...')
        // Ne s'applique que s'il s'agit d'une glose courte (maximum 8 mots) et ne contenant pas de verbes/phrases d'analyse exégétique
        const colonMatch = restText.match(/^(\s*:\s*)([a-zà-ÿ«][^.\n]{1,60}\.?)\s*$/i);
        const wordsAfterColon = colonMatch ? colonMatch[2].trim().split(/\s+/) : [];
        const isExegeticalSentence = colonMatch && (/\b(?:grec|verbe|apôtre|auteur|signifie|veut|saint|évangile|passage)\b/i.test(colonMatch[2]) || wordsAfterColon.length > 8);
        if (colonMatch && !isExegeticalSentence) {
          parenTransNode = (
            <span className="font-body text-ink-primary dark:text-neutral-200 inline">
              <span className="text-rubric-red/70 font-serif font-bold mr-1">:</span>
              <em className="font-body italic text-ink-secondary/90 dark:text-neutral-400 text-[0.95em] mr-1 inline" title="Explication ou traduction de Fillion">
                {renderInlineContent(colonMatch[2].trim())}
              </em>
            </span>
          );
          newRestText = '';
        } else {
          // 3. Rétrocompatibilité : si juste précédé par (vers. N).
          const versMatch = restText.match(/^(\s*\(vers\.\s*\d+\)\.?\s*)(.*)/i);
          if (versMatch) {
            parenTransNode = (
              <span className="font-body text-ink-primary dark:text-neutral-200 inline">
                <span className="text-rubric-red/80 font-semibold text-[0.9em] mr-1.5">{versMatch[1].trim()}</span>
                <em className="font-body italic text-ink-secondary/90 dark:text-neutral-400 text-[0.95em] mr-1.5 inline" title="Traduction en français de Fillion">
                  {versMatch[2]}
                </em>
              </span>
            );
            newRestText = '';
          }
        }
      }
    }

    return (
      <div key={'seg-' + index} className={index > 0 ? "mt-2 pt-0.5" : "mt-1"}>
        {index > 0 && <span className="text-rubric-red font-serif font-bold mr-2 select-none">—</span>}
        {prefixNum && (
          <span className="font-heading font-semibold text-rubric-red dark:text-red-400 mr-1.5">
            {prefixNum.replace(/\*/g, '')}
          </span>
        )}
        <span
          className="font-serif italic font-bold text-rubric-red dark:text-red-400 tracking-wide mr-1.5 inline"
          style={{ fontStyle: 'italic', fontWeight: '700' }}
          title="Lemme latin de la Vulgate"
        >
          {lemmaText}
        </span>
        {showTrans && (
          <span className="font-body italic text-ink-secondary/90 dark:text-neutral-400 text-[0.93em] ml-1 mr-1.5" title="Traduction en français de Fillion">
            [<em>{translation}</em>]
          </span>
        )}
        {parenTransNode}
        {newRestText && renderInlineContent(newRestText)}
      </div>
    );
  }

  // Segment ordinaire
  return (
    <div key={'seg-' + index} className={index > 0 ? "mt-2 pt-0.5" : "mt-1"}>
      {index > 0 && <span className="text-rubric-red font-serif font-bold mr-2 select-none">—</span>}
      {renderInlineContent(trimmed)}
    </div>
  );
}

/**
 * Composant principal de formattage typographique et de traduction des commentaires Fillion.
 */
export default function FormattedCommentary({ text }) {
  const renderedContent = useMemo(() => {
    if (!text) return null;

    // Règle: Nettoyer les astérisques asymétriques (*titre** ou **titre*) en gras propre (**titre**)
    // S'assurer de ne pas fusionner une balise fermante ** suivie d'une balise ouvrante * (ex: **2.** *Texte*)
    let sanitizedText = text
      .replace(/(?<!\*)\*([^*\s\n][^*\n]*?)\*\*(?!\*)/g, '**$1**')
      .replace(/(?<!\*)\*\*([^*\s\n][^*\n]*?)\*(?!\*)/g, (match, p1, offset, string) => {
        // Si précédé immédiatement par **N.** ou suivi de texte, ne pas corrompre
        if (/^\d+\.\s*$/.test(p1)) return match;
        return `**${p1}**`;
      });

    // Élimination défensive d'un incipit de verset intégral redondant (*Texte latin complet du verset* [Texte français complet] .)
    // quand il est immédiatement suivi d'un segment d'exégèse commençant par ' — ' ou d'une nouvelle ligne
    const fullVerseIncipitMatch = sanitizedText.match(/^\s*\*([^*]{35,})\*\s*\[([^\]]{35,})\]\s*(?:\.\s*|\s*)(?:—\s*|\s*:\s*|\s*)([\s\S]*)$/);
    if (fullVerseIncipitMatch && fullVerseIncipitMatch[1].split(/\s+/).length >= 8) {
      sanitizedText = fullVerseIncipitMatch[3].replace(/^[—–-]\s*/, '').trim();
    }

    // Nettoyage systématique des espaces orphelines avant un signe de ponctuation (ex: 'mot .' -> 'mot.', 'mot )' -> 'mot)')
    sanitizedText = sanitizedText.replace(/\s+([.,\)\]])/g, '$1');
    // S'assurer d'une espace entre un deux-points de titre en gras et une citation ouvrante (ex: 'accomplir :** *Quando' ou 'accomplir:** *Quando')
    sanitizedText = sanitizedText.replace(/:\*\*(?!\s)\*/g, ':** *');

    const paragraphs = sanitizedText.split(/\n\s*\n|\n(?=##|###|####|[A-Z0-9°\u2160-\u217F]+\.|\bSECTION\b|\bPREMIÈRE\b|\bDEUXIÈME\b)/);

    return paragraphs.map((para, pIdx) => {
      const cleanPara = para.trim();
      if (!cleanPara) return null;

      // Si c'est un titre markdown ou une en-tête / division majeure
      if (cleanPara.startsWith('## ') || cleanPara.startsWith('### ') || cleanPara.startsWith('#### ') ||
          /^(?:PREMIÈRE|DEUXIÈME|TROISIÈME|QUATRIÈME|CINQUIÈME)\s+PARTIE/i.test(cleanPara) ||
          /^SECTION\s+[IVXLCDM\u2160-\u217F]+/i.test(cleanPara) ||
          /^[A-ZÀÂÉÈÊËÎÏÔÖÙÛÜÇ\u2160-\u217F\s]{4,}$/.test(cleanPara) ||
          /^[A-ZÀÂÉÈÊËÎÏÔÖÙÛÜÇ0-9°\s\.\,\'\-–—\u2160-\u217F]+\.\s+[IVXLCDM\u2160-\u217F\d\s\.\,\-–—]+$/.test(cleanPara)) {
        return renderSegment(cleanPara, 0);
      }

      // Protéger les tirets à l'intérieur des intervalles bibliques (ex: 'IX, 1 — X, 44.')
      // pour éviter de scinder une phrase au milieu d'une référence
      let safePara = cleanPara.replace(/([IVXLCDM\d]+,\s*\d+)\s*[—–-]\s*([IVXLCDM\d]+,\s*\d+)/gi, function(match, p1, p2) {
        return p1 + ' \u2013 ' + p2;
      });

      // Split on em-dash (' — '), but NOT inside HTML span tags, bold blocks (**...**), parens (...) or brackets [...]
      // Strategy: temporarily replace protected blocks with placeholders, split, then restore.
      const protectedBlocks = [];
      const storeBlock = (m) => {
        const ph = '___PROT_BLOCK_' + protectedBlocks.length + '___';
        protectedBlocks.push(m);
        return ph;
      };

      let sanitizedPara = safePara
        .replace(/<span\s[^>]*>.*?<\/span>/g, storeBlock)
        .replace(/\*\*[\s\S]*?\*\*/g, storeBlock)
        .replace(/\([^\)\n]{1,300}\)/g, storeBlock)
        .replace(/\[[^\]\n]{1,300}\]/g, storeBlock);

      const rawSegments = sanitizedPara.split(/\s+—\s+/);
      const restoreBlocks = (str) => {
        let prev = '';
        let cur = str;
        while (cur !== prev && /___PROT_BLOCK_\d+___/.test(cur)) {
          prev = cur;
          cur = cur.replace(/___PROT_BLOCK_(\d+)___/g, (_, idx) => protectedBlocks[parseInt(idx, 10)]);
        }
        return cur;
      };
      const segments = rawSegments.map(s => restoreBlocks(s));

      return (
        <div key={'p-' + pIdx} className="mb-4 last:mb-0 leading-relaxed text-ink-primary dark:text-neutral-200">
          {segments.map((seg, sIdx) => renderSegment(seg, sIdx))}
        </div>
      );
    });
  }, [text]);

  return (
    <div className="formatted-fillion-commentary text-sm sm:text-base font-body space-y-3">
      {renderedContent}
    </div>
  );
}
